/**
 * Closing Module — 10-step closing workflow.
 *
 * Mirrors PropertyModule.jsx in shape:
 *   - STEPS array + deriveCurrentStep() are named exports (pure, no DB).
 *   - Default export is the overlay component that the borrower-profile
 *     Closings tab renders inside a full-screen modal.
 *
 * The overlay fetches its own detail (avoids coupling to whatever the
 * borrower profile already has loaded), owns its own handleAction, and
 * refetches on every successful action so the step bar reflects reality.
 *
 * The step derivation here is kept in lockstep with the server-side
 * deriveClosingStep() in backend/routes/closingModule.js so the list-page
 * step badge always matches what the action endpoints think.
 */

import Step6FigureCapture from './Step6FigureCapture';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ModuleNotes from './ModuleNotes';
import { STATUS_COLORS } from '../../constants/statusColors';
import Final1003Card from './Final1003Card';   // step 5: the Final 1003 against the CD (2026-10-27)
import { step5Buttons, ctcButton } from '../../utils/final1003Gate';   // step 5: Approve/Override wait for the Final 1003 check (push B)

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── Pure step definitions (mirrored on the server) ────────────────────────
// MI Certificate (legacy Step 7) is fully removed — Doorly's AFM™ is
// non-QM and doesn't bind MI. Cash to Close / Clear to Close / Funding
// renumbered 8/9/10 → 7/8/9.
//
// The underlying DB columns keep their original names
// (closing_module_steps.step8_complete, step9_complete, step10_*) —
// renaming them would break the deployed backend on rollout (same
// class of issue as the MI column drops). The UI maps:
//   Step 7 (Cash to Close)    -> cms.step8_complete
//   Step 8 (Clear to Close)   -> cms.step9_complete + cms.step9_closing_date
//   Step 9 (Funding)          -> cms.step10_ab_funded, cms.step10_bc_borrower_signed,
//                                cms.step10_bc_doorly_signed, cms.step10_warehouse_facility_id,
//                                cms.step10_bc_funding_requested_at
// Drop the legacy step_N column names when convenient in a future
// migration pass; the UI label↔column mapping is the canonical truth.
export const STEPS = [
  { id: 1, shortLabel: 'Title Order',        label: 'Title Order' },
  { id: 2, shortLabel: 'EMD & Down Payment', label: 'EMD & Down Payment' },
  { id: 3, shortLabel: 'Insp / Apprais.',    label: 'Appraisal & Inspection' },
  { id: 4, shortLabel: 'Compliance',         label: 'Compliance Check' },
  { id: 5, shortLabel: 'Final UW',           label: 'Final Underwriting' },
  { id: 6, shortLabel: 'Title / HOI / CD',   label: 'Title Commitment, HOI & Closing Disclosure' },
  { id: 7, shortLabel: 'Cash to Close',      label: 'Cash to Close' },
  { id: 8, shortLabel: 'Clear to Close',     label: 'Clear to Close' },
  { id: 9, shortLabel: 'Funding',            label: 'Funding' },
];

export function deriveCurrentStep({
  dcp, closingModuleSteps: cms, closingDisclosure: cd, optionPeriod: op, user,
}) {
  if (!dcp?.ab_title_receipt_confirmed_at || !dcp?.bc_title_receipt_confirmed_at) return 1;
  if (!cms?.step2_ab_complete || !cms?.step2_bc_complete) return 2;
  if (!op?.inspection_appraisal_ordered || !cms?.step3_admin_approved) return 3;
  if (!cms?.step4_complete) return 4;
  if (!cms?.step5_complete) return 5;
  if (!cms?.step6_title_commitment || !cms?.step6_hoi_reviewed || !cd?.cd_signed_at) return 6;
  // Mapping (see STEPS comment above): UI step 7/8/9 reads DB step8/9/10.
  if (!cms?.step8_complete && !cd?.funds_received) return 7;
  if (!cms?.step9_complete) return 8;
  if (!dcp?.funding_approved_at || !cms?.step10_bc_doorly_signed) return 9;
  return 'complete';
}

// ── Formatting helpers (kept local — same as the list page used) ──────────
const fmt$ = (val) => val
  ? '$' + parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  : '—';
const fmtDateTime = (val) => val
  ? new Date(val).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : null;

// ── Default export: overlay component ─────────────────────────────────────

// AI review of the borrower's HOI declaration page. Self-contained: owns
// its loading/verdict state, POSTs to /hoi-assess, and renders the verdict
// pill + extracted coverage + any issues. Advisory only — the admin still
// clicks Approve. Shows a persisted assessment (hoiDoc.hoi_ai_assessment)
// on mount so a prior run survives a refresh.
function HoiAnalyzeButton({ borrowerId, hoiDoc, API, hdrs }) {
  const persisted = (() => {
    try {
      return typeof hoiDoc?.hoi_ai_assessment === 'string'
        ? JSON.parse(hoiDoc.hoi_ai_assessment)
        : (hoiDoc?.hoi_ai_assessment || null);
    } catch (_) { return null; }
  })();
  const [assessment, setAssessment] = useState(persisted);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  const run = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/api/admin/borrowers/${borrowerId}/hoi-assess`, {}, { headers: hdrs });
      setAssessment(r.data?.assessment || null);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Analysis failed.');
    } finally {
      setBusy(false);
    }
  };

  const theme = ({
    acceptable:      { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0', label: '✓ Acceptable' },
    needs_attention: { bg: '#fffbeb', text: '#92400e', border: '#fde68a', label: '⚠ Needs Attention' },
    concern:         { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5', label: '! Concern' },
  })[assessment?.verdict] || null;

  const cov = assessment?.coverage || {};
  const covRows = [
    ['Carrier', cov.carrier], ['Policy #', cov.policyNumber],
    ['Dwelling (Cov A)', cov.dwellingCoverage], ['Deductible', cov.deductible],
    ['Effective', cov.effectiveDate], ['Expiration', cov.expirationDate],
    ['Mortgagee listed', cov.mortgageeListed], ['Property', cov.propertyAddress],
  ].filter(([, v]) => v);

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 6, width: assessment ? '100%' : 'auto' }}>
      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        <button onClick={run} disabled={busy}
          style={{ background: busy ? '#9ca3af' : '#acf44e', color: busy ? '#fff' : '#1a1a1a', border: 'none',
            borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: busy ? 'default' : 'pointer' }}>
          {busy ? 'Analyzing…' : assessment ? '↻ Re-analyze' : '✨ Analyze'}
        </button>
        {theme && (
          <span style={{ fontSize: 11, fontWeight: 700, background: theme.bg, color: theme.text,
            border: `1px solid ${theme.border}`, borderRadius: 9999, padding: '2px 10px' }}>
            AI: {theme.label}
          </span>
        )}
      </span>
      {err && <span style={{ fontSize: 11, color: '#991b1b' }}>{err}</span>}
      {assessment && (
        <span style={{ display: 'block', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          padding: '10px 12px', fontSize: 11, color: '#374151', lineHeight: 1.6 }}>
          <span style={{ display: 'block', marginBottom: covRows.length ? 8 : 0 }}>{assessment.reasoning}</span>
          {covRows.length > 0 && (
            <span style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 4 }}>
              {covRows.map(([k, v]) => (
                <span key={k} style={{ display: 'block' }}>
                  <span style={{ color: '#9ca3af' }}>{k}: </span><b>{String(v)}</b>
                </span>
              ))}
            </span>
          )}
          {Array.isArray(assessment.issues) && assessment.issues.length > 0 && (
            <span style={{ display: 'block', marginTop: 8, color: '#991b1b' }}>
              {assessment.issues.map((it, i) => <span key={i} style={{ display: 'block' }}>• {it}</span>)}
            </span>
          )}
          <span style={{ display: 'block', marginTop: 8, color: '#9ca3af', fontStyle: 'italic' }}>
            Advisory only — verify before approving.
          </span>
        </span>
      )}
    </span>
  );
}

export default function ClosingModule({ borrower, onClose }) {
  const [detail, setDetail]   = useState(null);
  // 🔑 The Final 1003 check (Final1003Card): while it mismatches, Approve, the override and Clear to Close stay off.
  const [final1003, setFinal1003] = useState(null);
  // 🔑 Step 5 while the check is unavailable (push C item 3): the override's written reason, and Retry (a fresh read).
  const [s5OverrideReason, setS5OverrideReason] = useState('');
  const [final1003Retry, setFinal1003Retry] = useState(0);
  // 🔑 Clear for Closing while the check is unavailable (D2 A, Jesse 2026-11-01): the override's written reason.
  const [ctcOverrideReason, setCtcOverrideReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [warehouseFacilities, setWarehouseFacilities] = useState([]);
  // viewStep = the step the admin is *looking at*, which can differ from
  // detail.current_step (= the derived gate). Initialized null so the first
  // render after detail loads snaps to current_step via the useEffect below.
  // Lets admins click Back to review a completed step without being yanked
  // forward by the derivation.
  const [viewStep, setViewStep] = useState(null);

  // Step 8 (Clear to Close) form state
  const [closingDate, setClosingDate] = useState('');
  const [notaryName, setNotaryName]   = useState('');
  const [notaryDate, setNotaryDate]   = useState('');
  const [notaryTime, setNotaryTime]   = useState('');
  // Step 1 Resend Title Open Emails — per-action status so the admin gets
  // visual feedback. Shape: { state: 'idle'|'sending'|'done', ab?, bc? }.
  // ab/bc each carry { ok: bool, error?: string } from the backend response.
  const [resendState, setResendState] = useState({ state: 'idle' });
  // Step 9 (Funding) warehouse selection
  const [selectedFacility, setSelectedFacility] = useState('');

  // Step 4 — per-test ComplianceCheck results for the failed/warn case.
  // The card surfaces overall status + findings count, but until this
  // fetch ran the admin had no way to see WHY (which test failed and
  // its citation/measured value). Endpoint exists at
  // /api/admin/modules/closing/:userId/compliance-results; we hit it
  // lazily once a run_id is present.
  const [complianceResults, setComplianceResults] = useState(null);
  const [complianceResultsLoading, setComplianceResultsLoading] = useState(false);

  const token = localStorage.getItem('doorly_admin_token');
  const hdrs = { Authorization: `Bearer ${token}` };

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const [detRes, facRes] = await Promise.all([
        axios.get(`${API}/api/admin/modules/closing/${borrower.id}`, { headers: hdrs }),
        axios.get(`${API}/api/admin/credit-facilities`,              { headers: hdrs }),
      ]);
      setDetail(detRes.data);
      const wh = (facRes.data || []).filter(f => f.facility_type === 'warehouse' && f.active);
      setWarehouseFacilities(wh);
      const existing = detRes.data?.closing_module_steps?.step10_warehouse_facility_id;
      if (existing) setSelectedFacility(String(existing));
    } catch (err) {
      console.error('[ClosingModule] detail fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDetail(); }, [borrower.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // suppressNextSnap is checked + cleared by the SmartResume useEffect
  // below. It's set by handleAction BEFORE the refetch so the useEffect
  // sees it as soon as `detail` changes. A prior fix (b2cdf1a) tried to
  // pin viewStep AFTER fetchDetail resolved, but lost the race: by the
  // time React rendered with the new `detail` AND the new `viewStep`,
  // the useEffect closure still saw cs > viewStep (e.g. 7 > 4 on a
  // Step 4 re-run that left step4_complete=TRUE from a prior pass),
  // and the snap-forward overwrote the pin on the same render cycle.
  // A ref is order-independent and resolves before the next render
  // commits.
  const suppressNextSnap = useRef(false);

  // SmartResume — on first load + on every refetch that bumps current_step
  // forward, snap viewStep to follow. Doesn't snap backward, so admins who
  // intentionally navigated back stay where they are. The ref above lets
  // action-triggered refetches opt out of the snap, so re-running an
  // already-complete step (e.g. Step 4 ComplianceCheck after a prior
  // passing run) doesn't yank the admin to the derived current_step.
  useEffect(() => {
    if (!detail) return;
    const cs = detail.current_step;
    if (viewStep === null) {
      setViewStep(typeof cs === 'number' ? cs : STEPS.length);
      return;
    }
    if (suppressNextSnap.current) {
      suppressNextSnap.current = false;
      return;
    }
    if (typeof cs === 'number' && typeof viewStep === 'number' && cs > viewStep) {
      setViewStep(cs);
    }
  }, [detail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-fetch the per-test ComplianceCheck results. Only runs once a
  // compliance_run_id exists on the detail payload AND the overall status
  // is not 'passed' (passed runs have no findings; render skips this list).
  // Re-fetches when run_id changes (admin re-ran the audit).
  useEffect(() => {
    const runId = detail?.closing_module_steps?.compliance_run_id;
    const status = String(detail?.closing_module_steps?.compliance_status || '').toLowerCase();
    if (!runId || status === 'passed') { setComplianceResults(null); return; }
    let cancelled = false;
    setComplianceResultsLoading(true);
    axios
      .get(`${API}/api/admin/modules/closing/${borrower.id}/compliance-results`, { headers: hdrs })
      .then((r) => { if (!cancelled) setComplianceResults(r.data || null); })
      .catch(() => { if (!cancelled) setComplianceResults(null); })
      .finally(() => { if (!cancelled) setComplianceResultsLoading(false); });
    return () => { cancelled = true; };
  }, [detail?.closing_module_steps?.compliance_run_id, detail?.closing_module_steps?.compliance_status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Action dispatcher — internalized here (was in parent on the list page).
  // ⚠️ silent: the step-6 captures render the server's refusal INLINE, next to
  // the field that is missing. Alerting as well would show the same sentence
  // twice and push it into a dialog the operator has to dismiss to act on it.
  const handleAction = async (step, action, data = {}, { silent = false } = {}) => {
    try {
      const r = await axios.post(
        `${API}/api/admin/modules/closing/${borrower.id}/step/${step}/action`,
        { action, data },
        { headers: hdrs }
      );
      // Set the suppress-snap flag BEFORE fetchDetail so the useEffect
      // sees it on the next render. Combined with the explicit pin
      // below, this guarantees viewStep stays on the acting step after
      // any action — including re-runs on already-complete steps and
      // approvals that advance the loan to the next step.
      suppressNextSnap.current = true;
      await fetchDetail();
      setViewStep(step);
      return r.data;
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      if (!silent) alert(msg);
      const e = new Error(msg); e.code = err.response?.data?.code || null;
      throw e;
    }
  };

  // Chrome wrapper — mirrors PropertyModule header style.
  const Chrome = ({ children }) => (
    <div style={{ width: '100%' }}>
      <div style={{
        background: 'linear-gradient(135deg, #111827 0%, #1e3a5f 100%)',
        border: '1px solid #3a6ad9',
        borderBottom: 'none',
        borderRadius: '12px 12px 0 0',
        padding: '18px 22px',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: detail ? 16 : 0, gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#acf44e',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              marginBottom: 4,
            }}>
              Closing Module
            </div>
            <div style={{
              fontSize: 15, fontWeight: 600, color: '#fff',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {borrower.first_name} {borrower.last_name}
              {borrower.loan_number && (
                <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8, fontWeight: 400 }}>
                  · {borrower.loan_number}
                </span>
              )}
            </div>
          </div>
          {onClose && (
            <button onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none', borderRadius: 8,
                color: '#fff', padding: '6px 12px',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                flexShrink: 0,
              }}>
              ✕ Close
            </button>
          )}
        </div>

        {/* Progress bar — only render once detail has loaded so we have a current_step. */}
        {detail && (() => {
          const cs = detail.current_step;
          return (
            <div>
              <div style={{ display: 'flex', gap: 3, marginBottom: 6 }}>
                {STEPS.map(s => (
                  <div key={s.id} title={s.shortLabel}
                    style={{
                      flex: 1, height: 4, borderRadius: 999,
                      background: (cs === 'complete' || s.id < cs)
                        ? '#acf44e'
                        : s.id === cs ? '#3a6ad9' : 'rgba(255,255,255,0.15)',
                    }} />
                ))}
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>
                {cs === 'complete'
                  ? 'All steps complete'
                  : `Step ${cs} of ${STEPS.length}: ${cs === 3 && isNC ? 'Appraisal' : STEPS.find(s => s.id === cs)?.label}`}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Body */}
      <div style={{
        background: '#fff',
        border: '1px solid #3a6ad9',
        borderTop: 'none',
        borderRadius: '0 0 12px 12px',
        padding: '20px 22px',
      }}>
        {children}
      </div>
    </div>
  );

  if (loading) {
    return <Chrome><div style={{ color: '#6b7280', fontSize: 13 }}>Loading…</div></Chrome>;
  }
  if (!detail) {
    return <Chrome><div style={{ color: '#991b1b', fontSize: 13 }}>Failed to load closing detail.</div></Chrome>;
  }

  const { dcp, closing_disclosure: cd, closing_module_steps: cms, option_period: op, offer_request: offerRequest, title_contact: tc, user, emd_wire_request: emdWire, closing_wires: closingWires, dp_alert: dpAlert, scjw_flag: scjwFlag, final_uw_check: finalUwCheck, compliance_certificate: complianceCert, hoi_document: hoiDoc, docmagic_cd: dmCd, title_package: titlePackage, title_company: titleCompany } = detail;
  // Step 5's Approve / Override: off until the Final 1003 check responds, then only if it passes (utils/final1003Gate).
  const s5Buttons = step5Buttons(final1003, finalUwCheck, s5OverrideReason);

  // New construction = appraisal only. Builders don't allow third-party
  // home inspections during construction (the Inspectify integration at
  // services/inspectify.js:76 already short-circuits inspection_types to
  // ['hybrid_appraisal'] for NC), so Step 3 here drops the Inspection
  // card and renames the section accordingly. `is_new_construction` is
  // plumbed on offer_request by closingModule.js's GET /:userId.
  const isNC = !!(offerRequest?.is_new_construction || op?.is_new_construction);
  const currentStep = detail.current_step;
  // displayStep = what's rendered. Falls back to currentStep on first paint.
  const displayStep = viewStep ?? currentStep;
  // Helper for derived numeric ceiling (currentStep can be 'complete').
  const maxStep = typeof currentStep === 'number' ? currentStep : STEPS.length;

  // Inline sub-components — keep local to avoid creating module-level surface
  // for one-off internal helpers. Same shape as the old ClosingStepDetail.
  const ActionBtn = ({ label, onClick, color = '#3a6ad9', light = false, disabled = false, testId }) => (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      style={{
        background: disabled ? '#9ca3af' : (light ? '#fff' : color),
        color: light ? '#374151' : '#fff',
        border: light ? '1px solid #e5e7eb' : 'none',
        borderRadius: 7, padding: '6px 14px',
        fontSize: 12, fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
      }}>
      {label}
    </button>
  );
  const Card = ({ title, children, width = '100%', alertBorder = false }) => (
    <div style={{
      background: alertBorder ? '#fff5f5' : '#f9fafb',
      border:     alertBorder ? '2px solid #ff082f' : '1px solid #e5e7eb',
      borderRadius: 10, padding: '14px 16px', width, boxSizing: 'border-box',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700,
        color: alertBorder ? '#991b1b' : '#6b7280',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ color: '#111827', fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );
  const StatusDot = ({ ok, label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 4 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: ok ? '#16a34a' : '#d1d5db', flexShrink: 0 }} />
      <span style={{ color: ok ? '#166534' : '#6b7280' }}>{label}</span>
    </div>
  );

  return (
    <Chrome>
      {/* 🔑 SCJW LOAN PACKAGE BASELINE (Jesse, 2026-10-29): new LEs estimate $1,250; this file's issued LE
          showed less (or no SCJW line), so the CD keeps the disclosed figure. Staff confirm what SCJW
          bills or cure. Shown on every step: it is a closing-cost question for the whole file. */}
      {scjwFlag && (
        <div data-testid="closing-scjw-flag" style={{
          border: `2px solid ${STATUS_COLORS.adverse_pending.bg}`, borderRadius: 10,
          padding: '12px 16px', marginBottom: 14, background: '#ffffff',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <div style={{
            width: 20, height: 20, background: STATUS_COLORS.adverse_pending.bg, borderRadius: '50%',
            color: STATUS_COLORS.adverse_pending.text, fontSize: 12, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, marginTop: 1,
          }}>!</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: STATUS_COLORS.adverse_pending.text, marginBottom: 2 }}>
              SCJW loan document preparation
            </div>
            <div style={{ fontSize: 12, color: STATUS_COLORS.adverse_pending.text }}>{scjwFlag.message}</div>
          </div>
        </div>
      )}

      {/* ── STEP 1: Title Order ─────────────────────────────────────────── */}
      {displayStep ===1 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            Step 1 — Title Order
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Card title="A-B Close — Title File" width="50%">
              {/* 'Title Email Sent' row removed — redundant with Package Sent
                  (both reflect the same send event from sendTitleFilePackageById). */}
              <Row label="A-B Package Sent" value={fmtDateTime(dcp?.ab_package_sent_at)} />
              <StatusDot ok={!!dcp?.ab_title_receipt_confirmed_at}
                label={dcp?.ab_title_receipt_confirmed_at
                  ? `Receipt confirmed ${fmtDateTime(dcp.ab_title_receipt_confirmed_at)}`
                  : 'Awaiting receipt confirmation'} />
              {!dcp?.ab_title_receipt_confirmed_at && (
                <div style={{ marginTop: 10 }}>
                  <ActionBtn label="Confirm Receipt (Manual)" light
                    onClick={() => handleAction(1, 'confirm_receipt_ab')} />
                </div>
              )}
            </Card>
            <Card title="B-C Close — Title File" width="50%">
              {/* 'Title Email Sent' row removed — redundant with Package Sent. */}
              <Row label="B-C Package Sent" value={fmtDateTime(dcp?.bc_package_sent_at)} />
              <StatusDot ok={!!dcp?.bc_title_receipt_confirmed_at}
                label={dcp?.bc_title_receipt_confirmed_at
                  ? `Receipt confirmed ${fmtDateTime(dcp.bc_title_receipt_confirmed_at)}`
                  : 'Awaiting receipt confirmation'} />
              {!dcp?.bc_title_receipt_confirmed_at && (
                <div style={{ marginTop: 10 }}>
                  <ActionBtn label="Confirm Receipt (Manual)" light
                    onClick={() => handleAction(1, 'confirm_receipt_bc')} />
                </div>
              )}
            </Card>
          </div>
          <div style={{ marginTop: 12 }}>
            {/* Direct axios (not handleAction) so we can surface per-side
                results from the backend's { ab, bc } response shape — the
                generic action wrapper would only show a global success/error
                and silently swallow a per-side failure (e.g. B-C PSA not yet
                fully executed). */}
            <ActionBtn
              label={resendState.state === 'sending' ? 'Resending…' : 'Resend Title Open Emails'}
              light
              disabled={resendState.state === 'sending'}
              onClick={async () => {
                setResendState({ state: 'sending' });
                try {
                  const r = await axios.post(
                    `${API}/api/admin/modules/closing/${borrower.id}/step/1/action`,
                    { action: 'resend_title_emails', data: {} },
                    { headers: hdrs }
                  );
                  setResendState({
                    state: 'done',
                    ab: r.data?.ab || { ok: false, error: 'No response' },
                    bc: r.data?.bc || { ok: false, error: 'No response' },
                  });
                  await fetchDetail();
                } catch (err) {
                  setResendState({
                    state: 'done',
                    ab: { ok: false, error: err.response?.data?.error || err.message },
                    bc: { ok: false, error: err.response?.data?.error || err.message },
                  });
                }
              }} />
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 6 }}>
              The original title open emails fire automatically on each PSA execution. Use this button only when one didn't land.
            </div>
            {/* Per-side result strip — appears after the request returns.
                Each side gets a colored row: green check if ok, red ✕ + the
                backend's error reason when not. */}
            {resendState.state === 'done' && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {['ab', 'bc'].map((side) => {
                  const r = resendState[side];
                  if (!r) return null;
                  const sideLabel = side === 'ab' ? 'A-B' : 'B-C';
                  return (
                    <div key={side} style={{
                      fontSize: 11, fontWeight: 600,
                      color: r.ok ? '#166534' : '#991b1b',
                      background: r.ok ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${r.ok ? '#86efac' : '#fecaca'}`,
                      borderRadius: 6, padding: '6px 10px',
                    }}>
                      {r.ok
                        ? `✓ ${sideLabel} title open email sent`
                        : `✕ ${sideLabel} not sent — ${r.error || 'unknown error'}`}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STEP 2: EMD & Down Payment ──────────────────────────────────── */}
      {displayStep ===2 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            Step 2 — EMD & Down Payment
          </div>

          {/* Down-payment overdue alert — fires when the borrower's down
              payment hasn't been received within 1 business day of the
              contract date. Backend computes dp_alert; frontend just shows. */}
          {dpAlert && (
            <div style={{
              border: '2px solid #ff082f', borderRadius: 10,
              padding: '12px 16px', marginBottom: 14, background: '#fff5f5',
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <div style={{
                width: 20, height: 20, background: '#ff082f', borderRadius: '50%',
                color: '#fff', fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginTop: 1,
              }}>!</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 2 }}>
                  Down Payment Overdue
                </div>
                <div style={{ fontSize: 12, color: '#991b1b' }}>
                  Down payment has not been received within 1 business day of the contract date
                  {user?.date_contract && (
                    <span> ({new Date(user.date_contract).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })})</span>
                  )}. Follow up with the borrower immediately.
                </div>
              </div>
            </div>
          )}

          {/* Cards: Down Payment LEFT, EMD RIGHT — reordered so cash-in
              precedes cash-out, mirroring the actual money flow. */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Card title="B-C Close — Down Payment" width="50%" alertBorder={dpAlert}>
              <Row label="Down Payment Amount" value={fmt$(dcp?.bc_emd_amount)} />
              <Row label="Expected from" value={`${borrower.first_name} ${borrower.last_name}`} />
              <StatusDot ok={!!cms?.step2_bc_complete}
                label={cms?.step2_bc_complete ? 'Down payment received' : 'Awaiting down payment wire'} />
              {!cms?.step2_bc_complete && (
                <div style={{ marginTop: 10 }}>
                  <ActionBtn label="Mark Received (Manual)" light onClick={() => handleAction(2, 'mark_bc_received')} />
                </div>
              )}
            </Card>

            <Card title="A-B Close — EMD" width="50%">
              <Row label="EMD Amount" value={fmt$(dcp?.ab_emd_amount)} />
              <Row label="Title Company" value={tc?.company_name} />
              {/* Wire only auto-creates AFTER the borrower's down payment
                  lands ('2:mark_bc_received'). Before that — italic copy. */}
              {!cms?.step2_bc_complete ? (
                <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', marginTop: 8 }}>
                  EMD wire will be requested automatically after down payment is confirmed received.
                </div>
              ) : emdWire ? (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: emdWire.status === 'sent' ? '#16a34a'
                        : emdWire.status === 'approved' ? '#3a6ad9'
                          : '#f59e0b',
                      flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
                      Wire {emdWire.status === 'sent' ? 'Sent'
                        : emdWire.status === 'approved' ? 'Approved — Pending Send'
                          : 'Requested — Pending Approval'}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Ref: {emdWire.memo}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    Amount: ${(emdWire.amount_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                  {emdWire.sent_at && (
                    <div style={{ fontSize: 11, color: '#16a34a' }}>
                      Sent {new Date(emdWire.sent_at).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                      })}
                    </div>
                  )}
                  <div style={{ marginTop: 6 }}>
                    <a href="/admin/finance/wires"
                      style={{ fontSize: 11, color: '#3a6ad9', fontWeight: 600, textDecoration: 'none' }}>
                      View in Finance →
                    </a>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', marginTop: 8 }}>
                  Wire creation pending — re-trigger from here if it didn't land.
                </div>
              )}
              {/* 'Mark Step Complete' button removed — when the wire flips
                  to 'sent' on the Finance page, the backend's /sent endpoint
                  auto-flips cms.step2_ab_complete and fires the funding
                  notification (see backend/routes/agents.js sent handler). */}
            </Card>
          </div>

          {/* Funding Notification — advance heads-up to the capital partner.
              Status is on dcp.closing_notice_sent_at; the helper that flips
              it auto-fires when the loan enters 'closing' stage. Resend
              clears the stamp + re-fires sendClosingNotice. */}
          <div style={{ marginTop: 12 }}>
            <Card title="Funding Notification — Acquisition Capital Partner">
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                Advance notice sent to the acquisition capital partner ahead of the formal funding request.
              </div>
              <StatusDot ok={!!dcp?.closing_notice_sent_at}
                label={dcp?.closing_notice_sent_at
                  ? `Notice sent ${fmtDateTime(dcp.closing_notice_sent_at)}`
                  : 'Notice not yet sent — fires automatically when borrower down payment received'} />
              {/* Manual trigger is always available now (covers historical
                  data where the auto-fire trigger didn't exist yet, and any
                  case where sendClosingNotice silently bailed). The action
                  uses the same '2:resend_closing_notice' handler since it
                  NULLs the stamp before calling sendClosingNotice. */}
              <div style={{ marginTop: 10 }}>
                <ActionBtn
                  label={dcp?.closing_notice_sent_at ? 'Resend Notice' : 'Send Notice Now'}
                  light
                  onClick={() => handleAction(2, 'resend_closing_notice')} />
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ── STEP 3: Appraisal (& Inspection for non-NC) ─────────────────── */}
      {displayStep ===3 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            Step 3 — {isNC ? 'Appraisal' : 'Appraisal & Inspection'}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {!isNC && (
              <Card title="Inspection" width="50%">
                <StatusDot ok={!!op?.inspection_appraisal_ordered}
                  label={op?.inspection_appraisal_ordered
                    ? `Ordered ${fmtDateTime(op.inspection_appraisal_ordered_at) || ''}`
                    : 'Not yet ordered'} />
                {op?.inspectify_order_id && <Row label="Order ID" value={op.inspectify_order_id} />}
                {op?.inspectify_collect_url && (
                  <div style={{ marginTop: 8 }}>
                    <a href={op.inspectify_collect_url} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 11, color: '#3a6ad9', fontWeight: 600 }}>
                      View Inspectify Order →
                    </a>
                  </div>
                )}
              </Card>
            )}
            <Card title="Appraisal (Traditional)" width={isNC ? '100%' : '50%'}>
              <StatusDot ok={!!op?.inspection_appraisal_ordered}
                label={op?.inspection_appraisal_ordered ? 'Appraisal ordered (Traditional 1004)' : 'Not yet ordered'} />
              {op?.inspectify_appraisal_id && <Row label="Appraisal ID" value={op.inspectify_appraisal_id} />}
              {/* Borrower acknowledgement of the appraisal report. Stamped
                  when the borrower clicks 'Mark as Reviewed' on the
                  Under Contract dashboard — that click is also Doorly's
                  proof of HPML appraisal-copy delivery, so it surfaces
                  here alongside the ordered/scheduled status. */}
              {op?.report_review_status === 'complete' ? (
                <Row label="Borrower Reviewed"
                  value={op.report_review_completed_at
                    ? `✓ ${fmtDateTime(op.report_review_completed_at)}`
                    : '✓ Reviewed'} />
              ) : (op?.appraisal_status === 'complete' || op?.appraisal_report_text) ? (
                <Row label="Borrower Review" value="Pending borrower acknowledgement" />
              ) : null}
              {isNC && (
                <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
                  Appraisal only — new construction skips the third-party home inspection.
                </div>
              )}
            </Card>
          </div>
          {/* Property Access & Listing Agent — read-only mirror of the
              Property Module Step 8 capture, surfaced here so the closing
              admin can confirm scheduling details with the listing agent
              without flipping to the Property tab. Pulls from
              option_periods.inspectify_availability_* + inspectify_access_*
              (saved by InspectifyAccessStep) and offer_requests.listing_*. */}
          {(() => {
            const availType = op?.inspectify_availability_type;
            const availWindows = Array.isArray(op?.inspectify_availability_windows)
              ? op.inspectify_availability_windows : [];
            const availContact = op?.inspectify_availability_contact || {};
            const availLatest = op?.inspectify_availability_latest_date;
            const accessTypes = Array.isArray(op?.inspectify_access_types)
              ? op.inspectify_access_types : [];
            const accessDetails = op?.inspectify_access_details || {};
            // Listing agent — current offer wins; falls back to disclosure if needed.
            const laName  = offerRequest?.listing_agent_name;
            const laEmail = offerRequest?.listing_agent_email;
            const laPhone = offerRequest?.listing_agent_phone;
            const laFirm  = offerRequest?.listing_office_name;

            const ACCESS_LABEL = {
              combination_lockbox: 'Combination Lockbox',
              app_lockbox:         'App-Enabled Lockbox',
              meet_contact:        'Meet Contact On-Site',
              hidden_key:          'Hidden Key',
              community_access:    'Community Access',
              door_code:           'Door Code',
            };
            const AVAIL_LABEL = {
              appointment: 'By Appointment',
              flexible:    'Flexible (between two dates)',
              contact:     'Request availability from contact',
            };
            const fmtDate = (raw) => raw
              ? new Date(raw).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              : null;
            const hasAvail = !!(availType || availWindows.length);
            const hasAccess = accessTypes.length > 0;
            const hasLA = !!(laName || laEmail || laPhone);

            // Don't render an empty shell — only show when at least one
            // section has data. Until Property Module Step 8 is saved, this
            // row stays hidden so the existing Inspection/Appraisal cards
            // above stand alone.
            if (!hasAvail && !hasAccess && !hasLA) return null;

            return (
              <div style={{ marginTop: 12, display: 'flex', gap: 12 }}>
                {/* Left — Property Access (from Property Module Step 8).
                    Wraps both Availability + Access details into one card.
                    Rendered at width:50% so it sits beside the Listing
                    Agent card. */}
                <Card title="Property Access (from Property Module Step 8)" width="50%">
                  {hasAvail ? (
                    <div style={{ marginBottom: hasAccess ? 14 : 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                        Availability
                      </div>
                      {availType && (
                        <Row label="Type" value={AVAIL_LABEL[availType] || availType} />
                      )}
                      {availType === 'appointment' && availWindows.length > 0 && (
                        <div style={{ marginTop: 4 }}>
                          {availWindows.map((w, i) => (
                            <Row key={i}
                              label={i === 0 ? 'Windows' : ''}
                              value={`${fmtDate(w.date) || '—'} · ${w.start || '—'}–${w.end || '—'}`} />
                          ))}
                        </div>
                      )}
                      {availType === 'flexible' && availLatest && (
                        <Row label="Latest date" value={fmtDate(availLatest)} />
                      )}
                      {availType === 'contact' && (availContact.name || availContact.phone || availContact.email) && (
                        <>
                          {availContact.name  && <Row label="Contact name"  value={availContact.name} />}
                          {availContact.phone && <Row label="Contact phone" value={availContact.phone} />}
                          {availContact.email && <Row label="Contact email" value={availContact.email} />}
                        </>
                      )}
                    </div>
                  ) : null}

                  {hasAccess ? (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                        Access Method
                      </div>
                      {accessTypes.map((t) => {
                        const det = accessDetails[t] || {};
                        const parts = [];
                        if (det.code)          parts.push(`Code: ${det.code}`);
                        if (det.directions)    parts.push(det.directions);
                        if (det.contact_name)  parts.push(det.contact_name);
                        if (det.contact_phone) parts.push(det.contact_phone);
                        if (det.contact_email) parts.push(det.contact_email);
                        return (
                          <Row key={t}
                            label={ACCESS_LABEL[t] || t}
                            value={parts.length ? parts.join(' · ') : '—'} />
                        );
                      })}
                    </div>
                  ) : null}

                  {!hasAvail && !hasAccess && (
                    <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                      Not yet captured on Property Module Step 8.
                    </div>
                  )}
                </Card>

                {/* Right — Listing Agent contact. Pulled from offer_requests
                    first, falling back to seller_disclosures (where some
                    flows capture LA contact first). */}
                <Card title="Listing Agent" width="50%">
                  {hasLA ? (
                    <>
                      {laName  && <Row label="Name"  value={laName} />}
                      {laFirm  && <Row label="Firm"  value={laFirm} />}
                      {laEmail && <Row label="Email" value={laEmail} />}
                      {laPhone && <Row label="Phone" value={laPhone} />}
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                      No listing agent on file.
                    </div>
                  )}
                </Card>
              </div>
            );
          })()}

          <div style={{ marginTop: 12 }}>
            <StatusDot ok={!!cms?.step3_admin_approved}
              label={cms?.step3_admin_approved
                ? 'Admin approved — ready to advance'
                : 'Pending admin review and approval'} />
            {!cms?.step3_admin_approved && op?.inspection_appraisal_ordered && (
              <div style={{ marginTop: 8 }}>
                <ActionBtn label="Approve — Advance to Step 4"
                  onClick={() => handleAction(3, 'approve_admin')} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STEP 4: Compliance Check (shell) ────────────────────────────── */}
      {displayStep ===4 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            Step 4 — Compliance Check
          </div>
          {/* Two-mode render: rich card when a real (or seeded) compliance
              run has populated cms.compliance_*; placeholder otherwise.
              Status colors: passed → green, passed_with_findings → amber,
              failed → red. Findings count surfaces alongside status. */}
          {cms?.compliance_run_id ? (() => {
            const status = String(cms.compliance_status || '').toLowerCase();
            const findings = Number(cms.compliance_findings_count || 0);
            const isPassed = status === 'passed';
            const isFailed = status === 'failed';
            const statusColor = isPassed ? '#16a34a' : isFailed ? '#dc2626' : '#f59e0b';
            const statusBg    = isPassed ? '#f0fdf4' : isFailed ? '#fef2f2' : '#fffbeb';
            const statusBorder = isPassed ? '#86efac' : isFailed ? '#fecaca' : '#fde68a';
            const statusLabel = status === 'passed' ? 'Passed'
              : status === 'passed_with_findings' ? 'Passed with Findings'
              : status === 'failed' ? 'Failed'
              : 'Pending Review';
            return (
              <Card title="ComplianceCheck Audit Result">
                <div style={{
                  background: statusBg, border: `1px solid ${statusBorder}`,
                  borderRadius: 8, padding: '12px 14px', marginBottom: 12,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: statusColor, flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: statusColor }}>
                      {statusLabel}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: statusColor, marginTop: 4 }}>
                    {findings === 0
                      ? 'No findings — clean compliance audit.'
                      : `${findings} finding${findings === 1 ? '' : 's'} flagged. ${isFailed ? 'Must be remediated before advancing.' : 'See ComplianceCheck Certificate below for full finding detail.'}`}
                  </div>
                </div>
                <Row label="Check ID" value={cms.compliance_run_id} />
                <Row label="Audit Run" value={fmtDateTime(cms.compliance_completed_at)} />
                <Row label="Findings" value={String(findings)} />
                <Row label="Federal Compliance" value="✓ Reg Z — TRID, HOEPA, HPML, ATR + LE/CD tolerance (Non-QM)" />
                <Row label="State Compliance" value="✓ TX §302.001(d) + FL §687.04 (late-charge cap)" />
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 8, lineHeight: 1.5 }}>
                  Audited against federal lending regulations + state high-cost loan thresholds.
                </div>

                {/* Per-test findings — surfaces WHY the audit failed/warned.
                    Renders fail rows first, then warns; passes are
                    intentionally hidden to keep the list focused on
                    actionable items. Without this list, the card just
                    said 'N findings' and admins had no path to diagnose. */}
                {(!isPassed) && (
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                      Findings Detail
                    </div>
                    {complianceResultsLoading ? (
                      <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>Loading findings…</div>
                    ) : !complianceResults?.results?.length ? (
                      <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>No detailed findings on file for this run.</div>
                    ) : (
                      (() => {
                        const actionable = complianceResults.results.filter(
                          (r) => r.status === 'fail' || r.status === 'warn'
                        );
                        if (!actionable.length) {
                          return <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>No fail/warn rows on this run.</div>;
                        }
                        return actionable.map((r, i) => {
                          const isFail = r.status === 'fail';
                          return (
                            <div key={`${r.test_key}-${i}`}
                              style={{
                                padding: '10px 12px',
                                marginBottom: 8,
                                background: isFail ? '#fef2f2' : '#fffbeb',
                                border: `1px solid ${isFail ? '#fecaca' : '#fde68a'}`,
                                borderLeft: `4px solid ${isFail ? '#dc2626' : '#f59e0b'}`,
                                borderRadius: 7,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                                <span style={{
                                  fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em',
                                  padding: '2px 6px', borderRadius: 4,
                                  background: isFail ? '#dc2626' : '#f59e0b', color: '#fff',
                                }}>
                                  {r.status}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{r.test_key}</span>
                                {r.category && (
                                  <span style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', background: '#f3f4f6', padding: '1px 6px', borderRadius: 4 }}>{r.category}</span>
                                )}
                              </div>
                              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5, marginBottom: 4 }}>{r.detail}</div>
                              {(r.measured_value != null || r.threshold_value != null) && (
                                <div style={{ fontSize: 11, color: '#6b7280' }}>
                                  {r.measured_value != null && <span>Measured: <strong style={{ color: '#111827' }}>{r.measured_value}</strong></span>}
                                  {r.measured_value != null && r.threshold_value != null && <span style={{ margin: '0 6px', color: '#9ca3af' }}>·</span>}
                                  {r.threshold_value != null && <span>Threshold: <strong style={{ color: '#111827' }}>{r.threshold_value}</strong></span>}
                                </div>
                              )}
                              {r.citation && (
                                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                                  {r.citation}
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()
                    )}
                  </div>
                )}
                {/* Manual approve — only renders for non-fail results that
                    aren't already marked step4_complete. 'Passed' results
                    auto-advance server-side (heal-on-read at
                    closingModule.js:783). 'Warn' results don't auto-advance
                    (passing-with-findings still needs admin review per
                    audit policy), but once the admin has reviewed the
                    findings below they need a path to advance — this is
                    that button. 'Failed' results stay blocked: cure the
                    findings, then re-run. */}
                {!cms?.step4_complete && !isFailed && (
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                    <ActionBtn
                      label={isPassed
                        ? 'Approve — Advance to Step 5'
                        : 'Acknowledge Findings & Advance to Step 5'}
                      onClick={() => {
                        const findingsCount = Number(cms?.compliance_findings_count || 0);
                        if (!isPassed && findingsCount > 0) {
                          if (!window.confirm(
                            `Advance to Step 5 with ${findingsCount} compliance finding${findingsCount === 1 ? '' : 's'} on file? ` +
                            `The findings are non-blocking (status='warn'), but Doorly's audit policy keeps step4_complete off until an admin explicitly acknowledges them.`
                          )) return;
                        }
                        handleAction(4, 'mark_complete');
                      }}
                    />
                  </div>
                )}

                {/* Certificate — generated by the native ComplianceCheck
                    engine and auto-filed under Documents → Disclosures.
                    Always present when an audit has run; the else branch
                    is the safety net for the edge case where the cms row
                    was hand-seeded without an accompanying documents row.

                    A re-run of the audit may produce a check_id that
                    doesn't match the cert on file — the cert generator
                    refuses to mint when overall='fail' (or 'error', or
                    when the snapshot took the provisional odd-days APR
                    path), so a re-run that fails leaves the previous
                    cert as the most recent. The mismatch banner below
                    surfaces that case to admins. */}
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                    ComplianceCheck Certificate
                  </div>
                  {(() => {
                    if (!complianceCert) return null;
                    // cms.compliance_run_id is 'cc:<id>'; cert filename is
                    // 'Doorly_ComplianceCheck_<loanno>_<id>.pdf'. Both
                    // ids are integers — compare numerically so a leading-
                    // zero / int-vs-string mismatch never triggers a
                    // false-positive banner.
                    const cardCheckId = Number(String(cms.compliance_run_id || '').replace(/^cc:/, ''));
                    const certFile = String(complianceCert.original_name || complianceCert.filename || '');
                    const m = certFile.match(/_(\d+)\.pdf$/i);
                    const certCheckId = m ? Number(m[1]) : null;
                    if (!cardCheckId || !certCheckId || cardCheckId === certCheckId) return null;
                    const cardFailed = isFailed;
                    return (
                      <div style={{
                        marginBottom: 10, padding: '10px 12px',
                        background: '#fffbeb', border: '1px solid #fde68a',
                        borderLeft: '4px solid #f59e0b', borderRadius: 7,
                        fontSize: 11, color: '#92400e', lineHeight: 1.5,
                      }}>
                        <div style={{ fontWeight: 700, marginBottom: 2 }}>
                          Showing an earlier run (#{certCheckId}) — latest is #{cardCheckId}
                        </div>
                        <div>
                          {cardFailed
                            ? `Re-run #${cardCheckId} returned overall=failed and the cert generator refused to mint a new certificate. Cure the failed findings (see card above) and re-run; the cert below stays on file until then.`
                            : `Re-run #${cardCheckId} did not produce a new certificate (likely refused mint — overall non-certifiable or odd-days APR path). The card status above reflects the latest run.`}
                        </div>
                      </div>
                    );
                  })()}
                  {complianceCert ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 7 }}>
                      <span style={{ color: '#166534', fontWeight: 700 }}>✓</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>
                          {complianceCert.original_name || complianceCert.filename}
                        </div>
                        <div style={{ fontSize: 10, color: '#16a34a' }}>
                          Generated {fmtDateTime(complianceCert.upload_date)} · also in Documents → Disclosures
                        </div>
                      </div>
                      {/* View — the /view endpoint requires a Bearer JWT.
                          A plain <a target="_blank"> can't carry the header,
                          so we fetch as a blob with auth, then open the blob
                          URL in a new tab. Same pattern as Dashboard.js
                          handleViewCertificate. */}
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const token = localStorage.getItem('doorly_admin_token');
                            const r = await axios.get(
                              `${API}/api/admin/borrowers/${borrower.id}/documents/${complianceCert.id}/view`,
                              { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
                            );
                            const url = URL.createObjectURL(r.data);
                            window.open(url, '_blank', 'noopener');
                            setTimeout(() => URL.revokeObjectURL(url), 60000);
                          } catch (err) {
                            alert(err?.message || 'Unable to open certificate. Please try again.');
                          }
                        }}
                        style={{ fontSize: 11, color: '#3a6ad9', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        View →
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!window.confirm('Re-run the ComplianceCheck audit? The existing run will be replaced.')) return;
                          handleAction(4, 'run_compliance_check', { force: true });
                        }}
                        style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, background: 'none', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', padding: '4px 10px' }}
                      >
                        Re-run
                      </button>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>
                      Certificate not on file — re-run the audit to regenerate.
                      <div style={{ marginTop: 6 }}>
                        <ActionBtn label="Re-run Audit" light
                          onClick={() => {
                            if (!window.confirm('Re-run the ComplianceCheck audit? The existing run will be replaced.')) return;
                            handleAction(4, 'run_compliance_check', { force: true });
                          }} />
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })() : (
            // No audit yet — API trigger card. The native ComplianceCheck
            // engine audits the loan against federal regs (TILA/RESPA/HOEPA/
            // HMDA) + TX/FL state high-cost loan thresholds. The certificate
            // is generated locally (pdf-lib) when overall_status is pass/warn.
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                ComplianceCheck Audit
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14, lineHeight: 1.6 }}>
                Submits this loan to the ComplianceCheck engine for an automated audit against TILA, RESPA, HOEPA, HMDA, and TX/FL state high-cost loan thresholds. On success, the certificate is filed under Documents → Disclosures and the loan advances to Step 5.
              </div>
              <ActionBtn label="Run ComplianceCheck Audit"
                onClick={() => handleAction(4, 'run_compliance_check')} />
            </div>
          )}
        </div>
      )}

      {/* ── STEP 5: Final Underwriting — stale-doc re-check ─────────────── */}
      {displayStep ===5 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            Step 5 — Final Underwriting
          </div>
          {/* Pre-run state: explainer + Run Analysis CTA. The check itself
              is gated on cms.final_uw_run_at — the backend only returns
              finalUwCheck after the admin clicks Run, so the panel below
              this block only renders once the analysis has been kicked
              off. Re-running is a single click on the small Re-run button
              that appears alongside the results. */}
          {!cms?.final_uw_run_at && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                Final Underwriting Analysis
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, lineHeight: 1.6 }}>
                Runs a ReadyCheck-style pass against the closing underwriting
                guide. Checks every required document's freshness
                (paystubs, bank statements, W-2s, returns, ID, VOR/VOM) and
                surfaces any item that's stale or missing so it can be
                re-requested from the borrower before final approval.
              </div>
              <ActionBtn label="Run Final UW Analysis"
                onClick={() => handleAction(5, 'run_uw_analysis')} />
            </div>
          )}
          {cms?.final_uw_run_at && (
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>Last run {fmtDateTime(cms.final_uw_run_at) || 'just now'}</span>
              <ActionBtn label="Re-run Analysis" light
                onClick={() => handleAction(5, 'run_uw_analysis')} />
            </div>
          )}

          {/* Per-doc-type check rows. Color codes:
              green   = current (within threshold)
              amber   = stale (uploaded but past threshold)
              red     = missing (no row on file)
              Required items render with a darker dot + count toward blocking;
              optional items are informational only. */}
          {finalUwCheck && (
            <Card title={`Document Refresh Check — ${finalUwCheck.blocking_count} blocking`}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                {finalUwCheck.all_required_current
                  ? '✓ All required documents are current. Ready to advance.'
                  : `${finalUwCheck.blocking_count} required document${finalUwCheck.blocking_count === 1 ? '' : 's'} need attention before final approval.`}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {finalUwCheck.checks.map(item => {
                  const color = item.status === 'current' ? '#16a34a'
                    : item.status === 'stale' ? '#f59e0b'
                    : '#dc2626';
                  const bg = item.status === 'current' ? '#f0fdf4'
                    : item.status === 'stale' ? '#fffbeb'
                    : '#fef2f2';
                  const border = item.status === 'current' ? '#86efac'
                    : item.status === 'stale' ? '#fde68a'
                    : '#fecaca';
                  const subLabel = item.status === 'current'
                    ? `Last uploaded ${item.days_old}d ago (threshold ${item.threshold_days}d)`
                    : item.status === 'stale'
                      ? `STALE — ${item.days_old}d old, threshold ${item.threshold_days}d. Request fresh copy.`
                      : `MISSING — no document on file. Request from borrower.`;
                  const needsRequest = item.status !== 'current';
                  const alreadyRequested = !!item.pending_request;
                  return (
                    <div key={item.document_type} style={{
                      background: bg, border: `1px solid ${border}`,
                      borderRadius: 7, padding: '8px 12px',
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                    }}>
                      <div style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: color, flexShrink: 0, marginTop: 5,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
                            {item.label}
                            {!item.required && (
                              <span style={{ fontSize: 9, color: '#9ca3af', fontWeight: 500, marginLeft: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                optional
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
                            {item.status === 'current' ? 'Current' : item.status === 'stale' ? 'Stale' : 'Missing'}
                          </div>
                        </div>
                        <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                          {subLabel}
                        </div>
                        {needsRequest && (
                          <div style={{ marginTop: 6 }}>
                            {alreadyRequested ? (
                              <div style={{ fontSize: 10, color: '#3a6ad9', fontWeight: 600, fontStyle: 'italic' }}>
                                ✉ Requested {fmtDateTime(item.pending_request.created_at) || ''} — awaiting borrower upload
                              </div>
                            ) : (
                              <button
                                onClick={() => handleAction(5, 'request_document', { document_type: item.document_type })}
                                style={{
                                  background: '#fff', color: '#374151',
                                  border: '1px solid #e5e7eb', borderRadius: 6,
                                  padding: '4px 10px', fontSize: 10, fontWeight: 600,
                                  cursor: 'pointer',
                                }}>
                                Request from Borrower →
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Approval state — green confirmation row whenever a prior
              approval is on file (step5_approved_at IS NOT NULL),
              regardless of step5_complete. The card is the historical
              audit trail; we keep it visible across re-runs so the
              admin sees "this was approved on X by Y" even after the
              re-run flipped step5_complete back to FALSE.
              ──────────────────────────────────────────────────────────
              Two visual states for the card:
                (a) step5_complete=TRUE  → green "Approved" (current)
                (b) step5_complete=FALSE → amber-tinted "Previously
                    Approved" with a re-approval-required hint
              ──────────────────────────────────────────────────────────
              Button visibility:
                step5_complete=TRUE  → no button (no "Re-approve")
                step5_complete=FALSE → "Approve Final Underwriting"
              The backend '5:run_uw_analysis' handler resets
              step5_complete=FALSE while preserving the timestamp pair,
              which is what makes state (b) reachable. */}
          {/* 🚨 Only a CHANGED result re-renders: the card sits inside Card/Chrome, which are re-created each render, so it
              remounts and re-reads on every render — storing an identical result would loop the read forever. */}
          <Final1003Card key={`f1003-${final1003Retry}`} API={API} userId={borrower.id} headers={hdrs}
            onState={(d) => setFinal1003((p) => (JSON.stringify(p) === JSON.stringify(d) ? p : d))} />

          {cms?.step5_approved_at && (
            <div style={{
              marginTop: 12, padding: '10px 14px',
              background: cms?.step5_complete ? '#f0fdf4' : '#fffbeb',
              border:  cms?.step5_complete ? '1px solid #86efac' : '1px solid #fde68a',
              borderLeft: cms?.step5_complete ? '4px solid #16a34a' : '4px solid #d97706',
              borderRadius: 8,
              display: 'flex', alignItems: 'center', gap: 10,
              fontSize: 12, color: cms?.step5_complete ? '#166534' : '#92400e',
            }}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>{cms?.step5_complete ? '✓' : '↻'}</span>
              <div>
                <div style={{ fontWeight: 700 }}>
                  {cms?.step5_complete ? 'Final Underwriting Approved' : 'Final Underwriting — Previously Approved'}
                </div>
                <div style={{ fontSize: 11, color: cms?.step5_complete ? '#15803d' : '#b45309', marginTop: 2 }}>
                  Approved {fmtDateTime(cms.step5_approved_at)}
                  {cms.step5_approved_by && <> · by {cms.step5_approved_by}</>}
                  {!cms?.step5_complete && <> · re-approval required after analysis re-run</>}
                </div>
              </div>
            </div>
          )}
          {/* Pre-audit-trail edge case — step5_complete=TRUE but no
              timestamp on file (legacy approval that happened before
              we started writing the audit pair). Keep the green
              acknowledgement so the UI still reflects the approved
              state, just without the timestamp line. */}
          {cms?.step5_complete && !cms?.step5_approved_at && (
            <div style={{
              marginTop: 12, padding: '10px 14px',
              background: '#f0fdf4', border: '1px solid #86efac',
              borderLeft: '4px solid #16a34a', borderRadius: 8,
              display: 'flex', alignItems: 'center', gap: 10,
              fontSize: 12, color: '#166534',
            }}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>✓</span>
              <div>
                <div style={{ fontWeight: 700 }}>Final Underwriting Approved</div>
                <div style={{ fontSize: 11, color: '#15803d', marginTop: 2 }}>
                  Step 5 complete (pre-audit-trail approval — no timestamp on file)
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Button surface only when NOT currently approved. After
                approval the card is the only acknowledgement; a "Re-
                approve" button is noisy and invites a second
                signature on a state that didn't change. The Re-run
                Analysis button (rendered upstream when final_uw_run_at
                is set) is the documented path back to an unapproved
                state. */}
            {/* 🚨 Both wait for the Final 1003 check (utils/final1003Gate, push B 2026-10-31): off and labelled
                "Checking Final 1003…" until it responds, back only if it passes. handleAction has already alerted a
                refusal; the catch keeps its rethrow from surfacing as an uncaught error. */}
            {!cms?.step5_complete && (
              <ActionBtn label={s5Buttons.approve.label} testId="step5-approve"
                disabled={s5Buttons.approve.disabled}
                onClick={() => handleAction(5, 'approve').catch(() => {})} />
            )}
            {/* 🚨 "Check unavailable, retry" (push C item 3): Retry re-runs the Final 1003 check (a fresh read). */}
            {!cms?.step5_complete && s5Buttons.retry.show && (
              <ActionBtn label="Retry" light testId="step5-final-1003-retry"
                onClick={() => { setFinal1003(null); setFinal1003Retry((n) => n + 1); }} />
            )}
            {!cms?.step5_complete && s5Buttons.override.show && (
              <ActionBtn label={s5Buttons.override.label} light testId="step5-override"
                disabled={s5Buttons.override.disabled}
                onClick={() => {
                  const docsLine = finalUwCheck && !finalUwCheck.all_required_current
                    ? `${finalUwCheck.blocking_count} required document${finalUwCheck.blocking_count === 1 ? ' is' : 's are'} stale or missing. ` : '';
                  if (!window.confirm(s5Buttons.override.needsReason
                    ? `Override while the Final 1003 check is unavailable?\n\n${docsLine}Your reason is recorded on the file with your name and the time. Only do this if you've confirmed the 1003 against the CD another way.`
                    : `Override the document refresh check?\n\n${docsLine}Overriding will mark Step 5 complete but the underlying issues remain — only do this if you've confirmed the docs another way.`
                  )) return;
                  handleAction(5, 'approve', s5Buttons.override.needsReason ? { force: true, override_reason: s5OverrideReason.trim() } : { force: true }).catch(() => {});
                }} />
            )}
          </div>
          {!cms?.step5_complete && s5Buttons.override.needsReason && (
            <div data-testid="step5-final-1003-unavailable" style={{ marginTop: 8, display: 'grid', gap: 6 }}>
              <div style={{ fontSize: 11, color: '#991b1b' }}>
                The Final 1003 check is unavailable{final1003 && final1003.failed ? ' (it could not be read)' : ' on this file'}. Approve stays off until it runs; Retry runs it again. Override &amp; Approve Anyway needs a written reason, recorded with your name and the time.
              </div>
              <textarea data-testid="step5-override-reason" aria-label="Reason for the override" rows={2} value={s5OverrideReason}
                onChange={(e) => setS5OverrideReason(e.target.value)} placeholder="Why approve without the Final 1003 check (at least 10 characters)"
                style={{ fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid #e5e7eb', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
          )}
          {!cms?.step5_complete && finalUwCheck && !finalUwCheck.all_required_current && (
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 6, fontStyle: 'italic' }}>
              Approve button is disabled until required docs are current. Override only with admin discretion.
            </div>
          )}
          {!cms?.step5_complete && s5Buttons.bad && (
            <div data-testid="final-1003-blocks" style={{ fontSize: 10, color: '#991b1b', marginTop: 6, fontStyle: 'italic' }}>
              Both off while the Final 1003 check has a mismatch. The override still covers the other checks it covers today.
            </div>
          )}
        </div>
      )}

      {/* ── STEP 6: Title / HOI / CD ─────────────────────────────────────── */}
      {displayStep ===6 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            Step 6 — Title Commitment, HOI & CD
          </div>
          <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
            {/* 🚨 THE BOOLEAN IS GONE FROM THE DISPLAY. cms.step6_title_commitment
                records that somebody ticked a box; detail.step6_capture records
                whether the document and its figures are on file. Rendering the
                claim was how two files came to show "received" with no artifact. */}
            <Card title="Title Commitment">
              <Step6FigureCapture
                docKey="title_commitment"
                item={detail?.step6_capture?.title_commitment}
                documentId={detail?.step6_capture?.title_commitment?.documentId}
                action={(payload) => handleAction(6, 'confirm_title_commitment', payload, { silent: true })}
              />
            </Card>
            {/* 🔑 THE TAX CERTIFICATE HAD NO CARD, because nothing read it. The
                title company orders one on every Texas closing and it states the
                authoritative annual tax; cdMath has been estimating at a flat
                percentage of purchase price beside it. */}
            <Card title="Tax Certificate">
              <Step6FigureCapture
                docKey="tax_certificate"
                item={detail?.step6_capture?.tax_certificate}
                documentId={detail?.step6_capture?.tax_certificate?.documentId}
                action={(payload) => handleAction(6, 'capture_tax_certificate', payload, { silent: true })}
              />
            </Card>
            {/* 🔑 CC push N item 7: SCJW's per-file invoice for loan document preparation — the CD charges it. */}
            <Card title="SCJW Invoice (loan document preparation)">
              <Step6FigureCapture
                docKey="scjw_invoice"
                item={detail?.step6_capture?.scjw_invoice}
                documentId={detail?.step6_capture?.scjw_invoice?.documentId}
                action={(payload) => handleAction(6, 'capture_scjw_invoice', payload, { silent: true })}
              />
            </Card>
            <Card title="Homeowners Insurance (HOI)">
              <StatusDot ok={!!user?.hoi_reviewed_at}
                label={user?.hoi_reviewed_at
                  ? `HOI reviewed ${fmtDateTime(user.hoi_reviewed_at)}`
                  : 'HOI declaration pending review'} />
              {/* The borrower-uploaded declaration page must be on file
                  before the admin can approve — surface it (View) and gate
                  the Approve button on it. No document → nothing to review. */}
              <StatusDot ok={!!hoiDoc}
                label={hoiDoc
                  ? `Declaration page uploaded ${fmtDateTime(hoiDoc.upload_date) || ''}`
                  : 'Waiting on borrower to upload the HOI declaration page'} />
              {hoiDoc && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <ActionBtn label="View Declaration →" light
                    onClick={async () => {
                      try {
                        const resp = await axios.get(
                          `${API}/api/admin/borrowers/${borrower.id}/documents/${hoiDoc.id}/view`,
                          { headers: hdrs, responseType: 'blob' }
                        );
                        const url = URL.createObjectURL(resp.data);
                        window.open(url, '_blank', 'noopener');
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                      } catch (err) {
                        alert('Unable to open HOI declaration: ' + (err.response?.data?.error || err.message));
                      }
                    }} />
                  <HoiAnalyzeButton borrowerId={borrower.id} hoiDoc={hoiDoc} API={API} hdrs={hdrs} />
                </div>
              )}
              <Step6FigureCapture
                docKey="hoi_declaration"
                item={detail?.step6_capture?.hoi_declaration}
                documentId={detail?.step6_capture?.hoi_declaration?.documentId || hoiDoc?.id}
                action={(payload) => handleAction(6, 'approve_hoi', payload, { silent: true })}
              />
            </Card>
            {/* Closing Disclosure — auto-generates on Step 6 entry. Three
                visible states:
                  1. No docmagic row yet → "Generating CD…" + Regenerate fallback
                  2. Doc generated, not admin-approved → View link + Approve button
                  3. Admin-approved → View + signing status (waiting/signed)
                Borrower's cd_available flips TRUE only at state 3. */}
            <Card title="Closing Disclosure">
              {!dmCd ? (
                <>
                  {/* 🚨 NOTHING IS STORED UNTIL ISSUANCE. The CD used to generate
                      and persist on Step 6 entry, on placeholder figures. The
                      preview streams inline and writes nothing; Approve & Issue
                      generates the real one after the figures gate. */}
                  <StatusDot ok={false}
                    label="Not issued — preview streams, nothing is stored until you issue" />
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <ActionBtn label="Preview CD →" light
                      onClick={async () => {
                        try {
                          const resp = await axios.get(
                            `${API}/api/admin/borrowers/${borrower.id}/cd-preview`,
                            { headers: hdrs, responseType: 'blob' }
                          );
                          const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                          window.open(url, '_blank');
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        } catch (err) {
                          let msg = err.response?.data?.error || err.message;
                          // The error body arrives as a Blob because of responseType.
                          if (err.response?.data instanceof Blob) {
                            try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch (_) {}
                          }
                          alert('Unable to preview CD: ' + msg);
                        }
                      }} />
                    <ActionBtn label="Approve & Issue CD — Make Visible to Borrower"
                      onClick={() => {
                        if (!window.confirm('Issue this CD? It is generated now and the borrower is notified.')) return;
                        handleAction(6, 'approve_cd');
                      }} />
                  </div>
                </>
              ) : (
                <>
                  <StatusDot ok={!!dmCd.generated_at}
                    label={`CD generated ${fmtDateTime(dmCd.generated_at) || ''}`} />
                  <StatusDot ok={!!cms?.cd_admin_approved}
                    label={cms?.cd_admin_approved
                      ? `Admin approved ${fmtDateTime(cms.cd_admin_approved_at) || ''} — visible to borrower`
                      : 'Pending admin approval — not yet visible to borrower'} />
                  <StatusDot ok={!!cd?.cd_signed_at}
                    label={cd?.cd_signed_at
                      ? `Borrower signed ${fmtDateTime(cd.cd_signed_at)}`
                      : cms?.cd_admin_approved
                        ? 'Awaiting borrower CD signature'
                        : 'Borrower will sign after admin approval'} />
                  {cd?.cash_to_close_amount && (
                    <Row label="Cash to Close" value={fmt$(cd.cash_to_close_amount)} />
                  )}
                  <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/* Fetch the PDF with the admin Bearer token and pop it
                        as a blob URL. An <a href> won't work — the admin-pdf
                        route is requireAdmin'd and won't carry the token. */}
                    <ActionBtn label="View CD →" light
                      onClick={async () => {
                        try {
                          const resp = await axios.get(
                            `${API}/api/disclosures/admin-pdf/${borrower.id}/cd`,
                            { headers: hdrs, responseType: 'blob' }
                          );
                          const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                          window.open(url, '_blank');
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        } catch (err) {
                          alert('Unable to open CD: ' + (err.response?.data?.error || err.message));
                        }
                      }} />
                    {!cms?.cd_admin_approved && (
                      <ActionBtn label="Approve CD — Make Visible to Borrower"
                        onClick={() => {
                          if (!window.confirm('Approve this CD? The borrower will be notified and able to sign.')) return;
                          handleAction(6, 'approve_cd');
                        }} />
                    )}
                    <ActionBtn label="Regenerate" light
                      onClick={() => {
                        if (!window.confirm('Regenerate the CD? This clears the current approval and replaces the draft.')) return;
                        handleAction(6, 'regenerate_cd');
                      }} />
                  </div>
                </>
              )}
            </Card>

            {/* Title Package completeness checker, named for the file's ACTUAL
                title company (detail.title_company — utils/titleCardCompany.js:
                the assigned title_contacts row, else the default, Tiago). Pure
                read against Doorly's own documents table + cms/cd milestones
                (backend computes detail.title_package). Nothing is sent to a
                title company when a document is uploaded; this card lets the
                admin confirm every required item is in before authorizing close.
                The BluePrint push button and lock note (a stub: the route only
                logs, Workstream B) render ONLY when the file's title company is
                BluePrint. TODO(multiple-vendors): per-vendor send actions. */}
            {(() => {
              const tp = titlePackage || { ab: [], bc: [] };
              const allItems = [...(tp.ab || []), ...(tp.bc || [])];
              const requiredItems = allItems.filter(i => i.required);
              const pending = requiredItems.filter(i => !i.present);
              const complete = requiredItems.length > 0 && pending.length === 0;
              const companyName = titleCompany?.name || tc?.company_name || '';
              const showBluePrintStub = !!titleCompany?.isBluePrint;
              // BluePrint title files are opened in Step 1 (Workstream B). Until
              // both order IDs exist, the push action is disabled and stubbed.
              const bpReady = !!(dcp?.blueprint_ab_order_id && dcp?.blueprint_bc_order_id);

              const pushToBlueprint = async (documentType) => {
                try {
                  const { data } = await axios.post(
                    `${API}/api/admin/borrowers/${borrower.id}/blueprint-push-doc`,
                    { document_type: documentType, order: 'both' },
                    { headers: hdrs }
                  );
                  window.alert(data?.message || 'Saved — will push to BluePrint once title files are opened.');
                  await fetchDetail();
                } catch (e) {
                  window.alert(e?.response?.data?.error || e.message || 'Push failed.');
                }
              };

              const StatusBadge = ({ item }) => {
                const state = item.present ? 'uploaded' : (item.required ? 'pending' : 'na');
                const cfg = state === 'uploaded'
                  ? { bg: '#f0fdf4', color: '#16a34a', text: '✓ Uploaded' }
                  : state === 'pending'
                    ? { bg: '#fffbeb', color: '#92400e', text: 'Pending' }
                    : { bg: '#f9fafb', color: '#6b7280', text: 'Not Required' };
                return (
                  <span style={{
                    background: cfg.bg, color: cfg.color, fontSize: 10, fontWeight: 700,
                    padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
                    {cfg.text}
                  </span>
                );
              };

              const Row = ({ item }) => (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 12, padding: '8px 0', borderBottom: '1px solid #f0f0f0',
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{item.label}</div>
                    {item.present && item.uploaded_at && (
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                        Uploaded {fmtDateTime(item.uploaded_at) || ''}
                      </div>
                    )}
                    {item.key === 'lender_instructions' && showBluePrintStub && (
                      <div style={{ marginTop: 6 }}>
                        <span title={bpReady ? '' : 'Open BluePrint title files first (Step 1)'}
                          style={{ display: 'inline-block' }}>
                          <ActionBtn
                            label={item.present ? 'Re-send to BluePrint' : 'Generate & Send to BluePrint →'}
                            light={item.present}
                            disabled={!bpReady}
                            onClick={() => pushToBlueprint('closing_instructions')} />
                        </span>
                      </div>
                    )}
                  </div>
                  <StatusBadge item={item} />
                </div>
              );

              return (
                <Card title={companyName ? `Title Package — ${companyName}` : 'Title Package'}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, flex: 1 }}>
                      Confirm all required items are complete before closing.
                      {titleCompany && !titleCompany.assigned && (
                        <div style={{ marginTop: 4 }}>
                          No title company is assigned on this file yet; {companyName} is the default.
                        </div>
                      )}
                    </div>
                    <span style={{
                      background: complete ? '#acf44e' : '#ffdd86',
                      color: '#1a2e05', fontSize: 11, fontWeight: 700,
                      padding: '4px 12px', borderRadius: 999, whiteSpace: 'nowrap',
                    }}>
                      {complete ? 'Complete' : `Incomplete · ${pending.length} item${pending.length === 1 ? '' : 's'} pending`}
                    </span>
                  </div>

                  <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
                    A-B Transaction
                  </div>
                  {(tp.ab || []).map(item => <Row key={item.key} item={item} />)}

                  <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 14, marginBottom: 2 }}>
                    B-C Transaction
                  </div>
                  {(tp.bc || []).map(item => <Row key={item.key} item={item} />)}

                  {showBluePrintStub && !bpReady && (
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 12, fontStyle: 'italic' }}>
                      🔒 BluePrint title files not opened yet — pushes queue locally until Step 1 opens the order (Workstream B).
                    </div>
                  )}
                </Card>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── STEP 7: Cash to Close (was step 8; legacy MI cert step removed) ── */}
      {displayStep ===7 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            Step 7 — Cash to Close
          </div>
          {/* Cash-to-close breakdown — mirrors the panel on the borrower
              ClosingDisclosures.js page (the 'Cash to Close Breakdown'
              block at line ~427). Gross = cd.effective_cash_to_close,
              which the closingModule detail endpoint computes with the
              same fallback chain closing.js uses for the borrower:
                cd.cash_to_close_amount  →  cdMath.cashToClose  →
                user.atlas_cash_to_close
              Without that fallback the breakdown would stay blank from
              CD-generation through wire-sent, since cash_to_close_amount
              is only written when the borrower clicks 'I've sent the
              wire' on their own dashboard — exactly backwards from when
              the admin actually needs this view (BEFORE the wire goes
              out, to confirm amount + reference with the borrower).
              ──────────────────────────────────────────────────────────
              dpSent = down_payment_amount_sent (only counted when admin
              has verified receipt — matches the borrower-side
              data?.down_payment_verified gate). Net = remaining to wire.
              Same monospace right-align + green negative-credit styling
              for visual parity across surfaces. */}
          {(() => {
            const grossCtc = cd?.effective_cash_to_close != null ? Number(cd.effective_cash_to_close) : null;
            const dpSent = user?.down_payment_verified && user?.down_payment_amount_sent
              ? Number(user.down_payment_amount_sent)
              : 0;
            const netCtc = grossCtc != null ? Math.max(0, grossCtc - dpSent) : null;
            // CTC wire reference — mirrors the closing-side reference
            // closing.js builds upstream and the WireInstructionsModal
            // renders to borrowers: 'CTC-<LOAN_NUMBER>-<LAST4>'. Same
            // string the title company keys the wire against, so an
            // admin reading the ref off the dashboard can match a
            // received wire to the loan file at a glance.
            const last4 = (user?.last_name || '').replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase() || 'XXXX';
            const wireRef = user?.loan_number ? `CTC-${user.loan_number}-${last4}` : null;
            return (
              <>
                {grossCtc != null && (
                  <Card title="Cash to Close Breakdown">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>Total Cash to Close</span>
                        <span style={{ fontFamily: 'monospace', color: '#111827' }}>{fmt$(grossCtc)}</span>
                      </div>
                      {dpSent > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#15803d' }}>
                          <span>Down Payment Already Sent</span>
                          <span style={{ fontFamily: 'monospace' }}>−{fmt$(dpSent)}</span>
                        </div>
                      )}
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        borderTop: '1px solid #e5e7eb',
                        marginTop: 4, paddingTop: 6,
                        fontWeight: 700, color: '#111827',
                      }}>
                        <span>Remaining to Wire</span>
                        <span style={{ fontFamily: 'monospace' }}>{fmt$(netCtc)}</span>
                      </div>
                    </div>
                    {/* Wire memo reference — same string the borrower
                        sees on their dashboard wire instructions modal,
                        same string the receiving bank posts against the
                        incoming wire. Surface it here so the admin can
                        confirm at a glance which loan a wire belongs to
                        without cross-referencing. */}
                    {wireRef && (
                      <div style={{
                        marginTop: 12, paddingTop: 10,
                        borderTop: '1px solid #f3f4f6',
                      }}>
                        <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                          Wire Memo / Reference
                        </div>
                        <div style={{
                          fontFamily: 'monospace', fontSize: 13,
                          fontWeight: 700, color: '#111827',
                          background: '#f9fafb', border: '1px solid #e5e7eb',
                          borderRadius: 6, padding: '6px 10px',
                          display: 'inline-block',
                        }}>
                          {wireRef}
                        </div>
                        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                          Same reference shown to the borrower on the wire instructions modal and posted by the bank against the incoming wire.
                        </div>
                      </div>
                    )}
                  </Card>
                )}
                {/* Vertical gap between the two Step 7 cards. Card itself
                    doesn't accept a style prop, so spacing lives on a
                    wrapper div. 16px matches the inter-card rhythm used
                    elsewhere in the closing module. */}
                <div style={{ marginTop: 16 }}>
                  <Card title="Cash to Close Confirmation">
                    <StatusDot ok={!!cd?.funds_received || !!cms?.step8_complete}
                      label={(cd?.funds_received || cms?.step8_complete)
                        ? 'Cash to close received'
                        : 'Awaiting cash to close — will auto-confirm via Mercury'} />
                    {!cd?.funds_received && !cms?.step8_complete && (
                      <div style={{ marginTop: 10 }}>
                        <ActionBtn label="Confirm Received (Manual)" light
                          onClick={() => handleAction(7, 'confirm_received')} />
                      </div>
                    )}
                  </Card>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ── STEP 8: Clear to Close (was step 9) ──────────────────────────── */}
      {displayStep ===8 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 16 }}>
            Step 8 — Clear to Close
          </div>
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Closing Date (minimum 3 business days)
            </div>
            <input type="date" value={closingDate}
              onChange={e => setClosingDate(e.target.value)}
              min={new Date(Date.now() + 3 * 86400000).toISOString().split('T')[0]}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, marginBottom: 12, display: 'block' }} />

            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, marginTop: 12 }}>
              B-C Mobile Notary
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {[
                { key: 'name', label: 'Notary Name', value: notaryName, set: setNotaryName, type: 'text' },
                { key: 'date', label: 'Notary Date', value: notaryDate, set: setNotaryDate, type: 'date' },
                { key: 'time', label: 'Notary Time', value: notaryTime, set: setNotaryTime, type: 'time' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>{f.label}</label>
                  <input type={f.type} value={f.value} onChange={e => f.set(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
          </div>
          {/* Outbound wires preview — visible BEFORE Clear for Closing.
              Doorly holds all client funds: the borrower wires their
              full cash-to-close (down payment + B-C remainder) to
              Doorly's escrow account upstream. At closing, Doorly is
              the sender on BOTH wires going to title — A-B (Doorly →
              Title for the A-B side, funded from Doorly's working
              capital / warehouse) AND B-C (Doorly → Title for the B-C
              side, equal to borrower's CD CTC minus the down payment
              already received).
              ──────────────────────────────────────────────────────────
              Renders the same numbers the post-Clear-for-Closing wire
              card shows (line ~1535) so the admin can verify the
              amounts BEFORE pressing Clear for Closing. After
              step9_complete the action card below takes over with
              wire status + Retry buttons. */}
          {(() => {
            const abAmount = dcp?.ab_cash_to_close != null ? Number(dcp.ab_cash_to_close) : null;
            // Mirror Step 7's CTC source-of-truth chain — effective_cash_to_close
            // falls back to cdMath when the borrower hasn't clicked
            // 'I've sent the wire' yet (which is the only event that
            // populates cd.cash_to_close_amount).
            const grossCtc = cd?.effective_cash_to_close != null
              ? Number(cd.effective_cash_to_close)
              : null;
            // Down payment received — same logic Step 7 uses.
            // Prefer the borrower-verified figure; fall back to the
            // contracted B-C EMD on the DCP record so the preview
            // still renders for borrowers whose DP hasn't been
            // formally verified yet (admin can spot the discrepancy
            // before the wire fires).
            const dpVerified = !!(user?.down_payment_verified && user?.down_payment_amount_sent);
            const dpSent = dpVerified
              ? Number(user.down_payment_amount_sent)
              : (dcp?.bc_emd_amount != null ? Number(dcp.bc_emd_amount) : 0);
            const bcNet = grossCtc != null ? Math.max(0, grossCtc - dpSent) : null;
            return (
              <div style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                padding: '14px 16px', marginBottom: 12,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: '#6b7280',
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
                }}>
                  Doorly → Title Wires (preview)
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12, lineHeight: 1.55 }}>
                  Doorly holds all client funds — all borrower wires come <em>to</em> Doorly.
                  At closing Doorly sends two outbound wires <em>to</em> Title:
                  one for the A-B side, one for the B-C side (equal to the borrower's CD
                  cash to close minus the down payment Doorly already received). These
                  fire as wire requests once you click <strong>Clear for Closing</strong>;
                  preview the amounts here first.
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <Card title="A-B Close — Doorly → Title" width="50%">
                    <Row label="Amount"
                      value={abAmount != null ? fmt$(abAmount) : '—'} />
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 6, lineHeight: 1.4 }}>
                      Doorly funds the A-B side from working capital / warehouse;
                      wire sent to A-B title agent at close.
                    </div>
                  </Card>
                  <Card title="B-C Close — Doorly → Title" width="50%">
                    <Row label="CD Cash to Close"
                      value={grossCtc != null ? fmt$(grossCtc) : '—'} />
                    <Row label="Less: Down Payment Received"
                      value={dpSent > 0 ? `(${fmt$(dpSent)})` : '—'} />
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 6, marginTop: 6 }}>
                      <Row label="Net Wire to Title"
                        value={bcNet != null ? fmt$(bcNet) : '—'} />
                    </div>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 6, lineHeight: 1.4 }}>
                      {dpVerified
                        ? 'Down payment verified by admin.'
                        : 'Down payment NOT yet verified — preview uses B-C EMD contract amount; verify receipt before wire fires.'}
                    </div>
                  </Card>
                </div>
              </div>
            );
          })()}

          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 14, lineHeight: 1.6 }}>
            Clicking Clear for Closing will send emails to: Title Company (escrow agent + CC), Borrower, Admin Alerts, and Jesse (A-B eClose instructions if eClose URL is on file).
          </div>
          {/* 🚨 D2 A (Jesse 2026-11-01): Clear for Closing reads the Final 1003 check itself (utils/final1003Gate ctcButton,
              backend utils/final1003Approve ctcGate). Unavailable → only with a written reason, recorded with your name and the time. */}
          <Final1003Card key={`f1003-ctc-${final1003Retry}`} API={API} userId={borrower.id} headers={hdrs}
            onState={(d) => setFinal1003((p) => (JSON.stringify(p) === JSON.stringify(d) ? p : d))} />
          {(() => {
            const cb = ctcButton(final1003, ctcOverrideReason, closingDate);
            return (
              <>
                {cb.needsReason && (
                  <div data-testid="ctc-final-1003-unavailable" style={{ margin: '8px 0', display: 'grid', gap: 6 }}>
                    <div style={{ fontSize: 11, color: '#991b1b' }}>
                      The Final 1003 check is unavailable{final1003 && final1003.failed ? ' (it could not be read)' : ' on this file'}. Retry runs it again. Clearing for closing without it needs a written reason, recorded with your name and the time.
                    </div>
                    <textarea data-testid="ctc-override-reason" aria-label="Reason for the override" rows={2} value={ctcOverrideReason}
                      onChange={(e) => setCtcOverrideReason(e.target.value)} placeholder="Why clear for closing without the Final 1003 check (at least 10 characters)"
                      style={{ fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid #e5e7eb', fontFamily: 'inherit', resize: 'vertical' }} />
                    <div><ActionBtn label="Retry" light testId="ctc-final-1003-retry"
                      onClick={() => { setFinal1003(null); setFinal1003Retry((n) => n + 1); }} /></div>
                  </div>
                )}
                <ActionBtn label={cb.label} color="#acf44e" testId="ctc-clear"
                  disabled={cb.disabled}
                  onClick={() => handleAction(8, 'clear_to_close', {
                    closing_date: closingDate, notary_name: notaryName, notary_date: notaryDate, notary_time: notaryTime,
                    ...(cb.needsReason ? { final1003_override: true, override_reason: ctcOverrideReason.trim() } : {}),
                  }).catch(() => {})} />
              </>
            );
          })()}

          {/* Wire Requests — gated on Clear to Close. Once issued, admin
              creates one wire per side. Memos: ref + "A-B Close" / "B-C Close"
              for unambiguous matching. Backend computes B-C as CD cash-to-
              close minus down payment received. */}
          {cms?.step9_complete ? (
            <div style={{ marginTop: 20 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#6b7280',
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
              }}>
                Closing Wire Requests
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                {(() => {
                  // Partition closing_wires by side based on memo prefix.
                  const abWire = (closingWires || []).find(w => w.memo?.includes('AB'));
                  const bcWire = (closingWires || []).find(w => w.memo?.includes('BC'));
                  const cdCTC = parseFloat(cd?.cash_to_close_amount || 0);
                  const dp = parseFloat(dcp?.bc_emd_amount || 0);
                  const netAmount = cdCTC - dp;

                  const renderWireStatus = (wire) => (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                        <div style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: wire.status === 'sent' ? '#16a34a'
                            : wire.status === 'approved' ? '#3a6ad9'
                              : '#f59e0b',
                          flexShrink: 0,
                        }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
                          {wire.status === 'sent' ? 'Sent'
                            : wire.status === 'approved' ? 'Approved — Pending Send'
                              : 'Requested — Pending Approval'}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Ref: {wire.memo}</div>
                      {wire.sent_at && (
                        <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2 }}>
                          Sent {new Date(wire.sent_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                          })}
                        </div>
                      )}
                      <div style={{ marginTop: 8 }}>
                        <a href="/admin/finance/wires"
                          style={{ fontSize: 11, color: '#3a6ad9', fontWeight: 600, textDecoration: 'none' }}>
                          View in Finance →
                        </a>
                      </div>
                    </>
                  );

                  return (
                    <>
                      <Card title="A-B Close — Wire to Title" width="50%">
                        <Row label="Amount" value={fmt$(dcp?.ab_cash_to_close)} />
                        {abWire ? renderWireStatus(abWire) : (
                          <div style={{ marginTop: 10 }}>
                            <ActionBtn label="Retry A-B Wire Request"
                              onClick={() => handleAction(8, 'create_ab_wire')} />
                          </div>
                        )}
                      </Card>
                      <Card title="B-C Close — Wire to Title" width="50%">
                        <Row label="CD Cash to Close"
                          value={cdCTC > 0 ? fmt$(cdCTC) : '—'} />
                        <Row label="Less: Down Payment Received"
                          value={dp > 0 ? `(${fmt$(dp)})` : '—'} />
                        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 6, marginTop: 6 }}>
                          <Row label="Net Wire Amount"
                            value={netAmount > 0 ? fmt$(netAmount) : '—'} />
                        </div>
                        {bcWire ? renderWireStatus(bcWire) : (
                          <div style={{ marginTop: 10 }}>
                            <ActionBtn label="Retry B-C Wire Request"
                              disabled={netAmount <= 0}
                              onClick={() => handleAction(8, 'create_bc_wire')} />
                            {netAmount <= 0 && (
                              <div style={{ fontSize: 10, color: '#991b1b', marginTop: 4 }}>
                                CD cash to close or down payment amount not set — check loan file
                              </div>
                            )}
                          </div>
                        )}
                      </Card>
                    </>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 16, fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>
              Wire requests are created automatically when Clear to Close is issued.
            </div>
          )}
        </div>
      )}

      {/* ── STEP 9: Funding (was step 10) ────────────────────────────────── */}
      {displayStep ===9 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            Step 9 — Funding
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Card title="A-B Funding — Oakhurst" width="50%">
              <StatusDot ok={!!dcp?.ab_funding_requested_at}
                label={dcp?.ab_funding_requested_at
                  ? `Funding request sent ${fmtDateTime(dcp.ab_funding_requested_at)}`
                  : 'Funding request not yet sent'} />
              <StatusDot ok={!!dcp?.funding_approved_at}
                label={dcp?.funding_approved_at
                  ? `Oakhurst approved ${fmtDateTime(dcp.funding_approved_at)}`
                  : 'Awaiting Oakhurst approval'} />
              {!dcp?.ab_funding_requested_at && (
                <div style={{ marginTop: 10 }}>
                  <ActionBtn label="Send A-B Funding Request"
                    onClick={() => handleAction(9, 'request_ab_funding')} />
                </div>
              )}
            </Card>
            <Card title="B-C Funding — Warehouse" width="50%">
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Select Warehouse Facility
                </label>
                <select value={selectedFacility}
                  onChange={e => {
                    setSelectedFacility(e.target.value);
                    if (e.target.value) {
                      handleAction(9, 'set_warehouse', { facility_id: parseInt(e.target.value) });
                    }
                  }}
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12 }}>
                  <option value="">Select…</option>
                  {warehouseFacilities.map(f => (
                    <option key={f.id} value={f.id}>
                      {f.lender_name} — ${(f.available_cents / 100).toLocaleString()} available
                    </option>
                  ))}
                </select>
              </div>
              <StatusDot ok={!!cms?.step10_bc_borrower_signed} label="Borrower signed closing docs" />
              <StatusDot ok={!!cms?.step10_bc_doorly_signed}  label="Doorly signed closing docs" />
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {!cms?.step10_bc_borrower_signed && (
                  <ActionBtn label="Confirm Borrower Signed" light
                    onClick={() => handleAction(9, 'confirm_bc_borrower_signed')} />
                )}
                {cms?.step10_bc_borrower_signed && !cms?.step10_bc_doorly_signed && (
                  <ActionBtn label="Confirm Doorly Signed" light
                    onClick={() => handleAction(9, 'confirm_doorly_signed')} />
                )}
                {cms?.step10_bc_doorly_signed && !cms?.step10_bc_funding_requested_at && (
                  <ActionBtn label="Request B-C Funding"
                    onClick={() => handleAction(9, 'request_bc_funding')} />
                )}
                {cms?.step10_bc_funding_requested_at && (
                  <div style={{ fontSize: 11, color: '#166534', fontWeight: 600, padding: '6px 0' }}>
                    ✓ B-C funding requested {fmtDateTime(cms.step10_bc_funding_requested_at)}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {dcp?.funding_approved_at && cms?.step10_bc_doorly_signed && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, marginBottom: 8 }}>
                All funding confirmed — ready to close loan
              </div>
              <ActionBtn label="Complete — Move to Closed" color="#acf44e"
                onClick={() => {
                  if (window.confirm('This will move the loan to Closed status and trigger the referral commission. Are you sure?')) {
                    handleAction(9, 'complete');
                  }
                }} />
            </div>
          )}
        </div>
      )}

      {/* Complete */}
      {displayStep ==='complete' && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '16px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#166534', marginBottom: 4 }}>Loan Closed</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            All closing steps complete.
            {borrower.date_closed && <span> Closed on {fmtDateTime(borrower.date_closed)}.</span>}
          </div>
        </div>
      )}

      {/* Module Notes — same convention as Property/Underwriting */}
      <div style={{ marginTop: 16 }}>
        <ModuleNotes moduleType="closing" borrowerId={borrower.id} />
      </div>

      {/* Footer nav — Back/Next mirrors PropertyModule pattern. Next is
          gated on viewStep < maxStep so admins can't navigate past the
          derived gate; Back is only blocked at step 1. */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb',
      }}>
        <button
          disabled={displayStep <= 1}
          onClick={() => setViewStep(s => Math.max(1, (s || 1) - 1))}
          style={{
            background: '#fff', color: '#374151',
            border: '1px solid #e5e7eb', borderRadius: 8,
            padding: '8px 18px', fontSize: 13, fontWeight: 600,
            cursor: displayStep <= 1 ? 'default' : 'pointer',
            opacity: displayStep <= 1 ? 0.4 : 1,
          }}>
          ← Back
        </button>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Step {typeof displayStep === 'number' ? displayStep : STEPS.length} of {STEPS.length}
          {currentStep === 'complete' && (
            <span style={{ marginLeft: 8, color: '#166534', fontWeight: 600 }}>· Complete</span>
          )}
        </div>
        <button
          disabled={displayStep >= maxStep && currentStep !== 'complete' ? true : displayStep >= STEPS.length}
          onClick={() => setViewStep(s => Math.min(maxStep, (s || 1) + 1))}
          style={{
            background: '#3a6ad9', color: '#fff',
            border: 'none', borderRadius: 8,
            padding: '8px 18px', fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
            opacity: displayStep >= maxStep ? 0.4 : 1,
          }}>
          Next →
        </button>
      </div>
    </Chrome>
  );
}
